"use client"

import { Card, CardContent } from "@/components/ui/card"
import { Shield, Eye, Lock, Users, FileText, Mail } from "lucide-react"
import { useEffect } from "react"

const sections = [
  {
    id: "information-we-collect",
    title: "Information We Collect",
    icon: FileText,
    content: [
      {
        subtitle: "Account Information",
        text: "When you create an account, we collect your name, email address, and password. If you sign up using a third-party service (like Google), we receive basic profile information from that service.",
      },
      {
        subtitle: "Design Data",
        text: "We collect the room photos you upload, design preferences you select, and the projects you create. This helps us provide personalized design recommendations and improve our AI models.",
      },
      {
        subtitle: "Usage Information",
        text: "We automatically collect information about how you use our service, including pages visited, features used, time spent on the platform, and technical information like your IP address, browser type, and device information.",
      },
      {
        subtitle: "Payment Information",
        text: "When you make a purchase, our payment processors collect your payment card information. We do not store complete payment card details on our servers.",
      },
    ],
  },
  {
    id: "how-we-use-information",
    title: "How We Use Your Information",
    icon: Eye,
    content: [
      {
        subtitle: "Provide Our Services",
        text: "We use your information to create your account, generate AI design recommendations, save your projects, and provide customer support.",
      },
      {
        subtitle: "Improve Our Platform",
        text: "We analyze usage patterns and design data to improve our AI models, develop new features, and enhance user experience. All data used for AI training is anonymized and aggregated.",
      },
      {
        subtitle: "Communication",
        text: "We may send you service-related emails, account notifications, and (with your consent) marketing communications about new features or design inspiration.",
      },
      {
        subtitle: "Legal Compliance",
        text: "We may use your information to comply with legal obligations, resolve disputes, and enforce our terms of service.",
      },
    ],
  },
  {
    id: "information-sharing",
    title: "Information Sharing and Disclosure",
    icon: Users,
    content: [
      {
        subtitle: "Service Providers",
        text: "We share information with trusted third-party service providers who help us operate our platform, including cloud hosting, payment processing, email delivery, and analytics services. These providers are contractually bound to protect your information.",
      },
      {
        subtitle: "Business Transfers",
        text: "If Roomvia is involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you of any such change in ownership or control.",
      },
      {
        subtitle: "Legal Requirements",
        text: "We may disclose your information if required by law, court order, or government request, or to protect the rights, property, or safety of Roomvia, our users, or others.",
      },
      {
        subtitle: "Public Content",
        text: "If you choose to share your designs publicly in our gallery, that content becomes publicly visible. You can control the privacy settings of your designs in your account settings.",
      },
    ],
  },
  {
    id: "data-security",
    title: "Data Security",
    icon: Lock,
    content: [
      {
        subtitle: "Security Measures",
        text: "We implement industry-standard security measures to protect your information, including encryption in transit and at rest, secure data centers, regular security audits, and access controls.",
      },
      {
        subtitle: "Data Retention",
        text: "We retain your personal information for as long as your account is active or as needed to provide services. You can delete your account at any time, and we will delete your personal information within 30 days, except where required by law.",
      },
      {
        subtitle: "Breach Notification",
        text: "In the unlikely event of a data breach that affects your personal information, we will notify you and relevant authorities as required by applicable law.",
      },
    ],
  },
  {
    id: "your-rights",
    title: "Your Rights and Choices",
    icon: Shield,
    content: [
      {
        subtitle: "Access and Portability",
        text: "You can access, update, or download your personal information through your account settings. You can also request a copy of your data by contacting us.",
      },
      {
        subtitle: "Deletion",
        text: "You can delete your account and personal information at any time. Some information may be retained in anonymized form for analytics and AI training purposes.",
      },
      {
        subtitle: "Marketing Communications",
        text: "You can opt out of marketing emails by clicking the unsubscribe link in any email or updating your preferences in your account settings.",
      },
      {
        subtitle: "Cookies and Tracking",
        text: "You can control cookies through your browser settings. Note that disabling certain cookies may affect the functionality of our service.",
      },
    ],
  },
  {
    id: "international-transfers",
    title: "International Data Transfers",
    icon: Mail,
    content: [
      {
        subtitle: "Global Operations",
        text: "Roomvia operates globally, and your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for international transfers.",
      },
      {
        subtitle: "Privacy Shield and Adequacy",
        text: "When transferring data from the EU or UK, we rely on adequacy decisions, standard contractual clauses, or other approved transfer mechanisms to ensure your data remains protected.",
      },
    ],
  },
]

export function PrivacyPolicyPage() {
  const lastUpdated = "December 9, 2024"
  // Calculate header height - our navbar is 64px (h-16)
  const HEADER_HEIGHT = 80 // Slightly more than h-16 (64px) to add some padding

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0)

    // Handle anchor links with smooth scrolling and proper offset
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (target.tagName === "A" && target.getAttribute("href")?.startsWith("#")) {
        e.preventDefault()
        const id = target.getAttribute("href")?.substring(1)
        const element = document.getElementById(id || "")

        if (element) {
          const yOffset = -HEADER_HEIGHT // Offset by header height plus some padding
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset
          window.scrollTo({ top: y, behavior: "smooth" })
        }
      }
    }

    document.addEventListener("click", handleAnchorClick)

    return () => {
      document.removeEventListener("click", handleAnchorClick)
    }
  }, [])

  return (
    <div className="container px-4 py-8 mx-auto md:px-6 md:py-12 max-w-4xl">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
        <p className="text-lg text-gray-600 mb-2">
          Your privacy is important to us. This policy explains how we collect, use, and protect your information.
        </p>
        <p className="text-sm text-gray-500">Last updated: {lastUpdated}</p>
      </div>

      {/* Quick Navigation */}
      <Card className="mb-8 bg-[#40A1AB]/5">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">Quick Navigation</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {sections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="text-[#40A1AB] hover:text-[#3A919A] transition-colors"
              >
                {section.title}
              </a>
            ))}
            <a href="#childrens-privacy" className="text-[#40A1AB] hover:text-[#3A919A] transition-colors">
              Children's Privacy
            </a>
            <a href="#third-party-services" className="text-[#40A1AB] hover:text-[#3A919A] transition-colors">
              Third-Party Services
            </a>
            <a href="#contact-us" className="text-[#40A1AB] hover:text-[#3A919A] transition-colors">
              Contact Us
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Introduction */}
      <Card className="mb-8">
        <CardContent className="p-6">
          <h2 className="text-2xl font-semibold mb-4">Introduction</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              Welcome to Roomvia. We respect your privacy and are committed to protecting your personal information.
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use
              our AI-powered interior design platform.
            </p>
            <p>
              By using Roomvia, you agree to the collection and use of information in accordance with this policy. If
              you do not agree with our policies and practices, please do not use our service.
            </p>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new
              Privacy Policy on this page and updating the "Last updated" date.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Main Sections */}
      <div className="space-y-8">
        {sections.map((section) => {
          const Icon = section.icon
          return (
            <Card
              key={section.id}
              id={section.id}
              className="scroll-mt-[80px]" // Increased to match header height
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-[#40A1AB]/10 p-2 rounded-lg">
                    <Icon className="h-6 w-6 text-[#40A1AB]" />
                  </div>
                  <h2 className="text-2xl font-semibold">{section.title}</h2>
                </div>

                <div className="space-y-6">
                  {section.content.map((item, index) => (
                    <div key={index}>
                      <h3 className="text-lg font-medium mb-2 text-gray-900">{item.subtitle}</h3>
                      <p className="text-gray-700 leading-relaxed">{item.text}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Children's Privacy */}
      <Card className="mt-8 scroll-mt-[80px]" id="childrens-privacy">
        <CardContent className="p-6">
          <h2 className="text-2xl font-semibold mb-4">Children's Privacy</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              Our service is not intended for children under the age of 13. We do not knowingly collect personal
              information from children under 13. If you are a parent or guardian and believe your child has provided us
              with personal information, please contact us immediately.
            </p>
            <p>
              If we discover that a child under 13 has provided us with personal information, we will delete such
              information from our servers immediately.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Third-Party Services */}
      <Card className="mt-8 scroll-mt-[80px]" id="third-party-services">
        <CardContent className="p-6">
          <h2 className="text-2xl font-semibold mb-4">Third-Party Services</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              Our service may contain links to third-party websites, products, or services that are not owned or
              controlled by Roomvia. We have no control over and assume no responsibility for the content, privacy
              policies, or practices of any third-party websites or services.
            </p>
            <p>
              We integrate with third-party services for authentication (Google), payment processing (Stripe), and
              analytics. These services have their own privacy policies, and we encourage you to review them.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card className="mt-8 scroll-mt-[80px]" id="contact-us">
        <CardContent className="p-6">
          <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>If you have any questions about this Privacy Policy, please contact us:</p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="font-medium">Roomvia Privacy Team</p>
              <p>Email: support@roomvia.ai</p>
            </div>
            <p>
              For EU residents: If you are located in the European Union, you have the right to lodge a complaint with
              your local data protection authority if you believe we have not addressed your concerns adequately.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
